<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, reactive, computed, watch } from 'vue';
import type { AuthUser } from '../../core/state/auth-store';
import {
  fetchHero, createHero, fetchStats, classLabel,
  type HeroProfile, type HeroClass, type EffectiveStats,
} from '../game/hero-api';
import {
  fetchInventory, SLOTS,
  type Inventory as GearInventory, type Artifact, type EquipSlot,
} from '../game/inventory-api';
import {
  CLASS_BASE_STATS, CLASS_STAT_ROWS, CLASS_STAT_LABEL_KEY, classStatPips,
} from '../game/class-stats';
import * as music from '../game/music';
import * as sfx from '../game/sfx';
import { tutorialLaunch } from '../game/tutorial-launch';
import SagaGuide from './SagaGuide.vue';
import Clans from './Clans.vue';
import wordmark from '../assets/fimbulwake/wordmark.png';
import liveStash from '../assets/fimbulwake/backgrounds/live-stash.mp4';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import nerveIcon from '../assets/fimbulwake/icons/nerve.webp';
import CityMap from './CityMap.vue';
import Districts from './Districts.vue';
import Tutorial from './Tutorial.vue';
import GoldReserve from './GoldReserve.vue';
import CountingHouse from './CountingHouse.vue';
import ToastHost from './ToastHost.vue';
import LevelUpBanner from './LevelUpBanner.vue';
import { resetObserved as resetLevelWatch } from '../game/level-up';
import ConfirmHost from './ConfirmHost.vue';
import { confirmAction } from '../game/confirm';
import { fightAbandon, fetchCurrentFight, type FightView } from '../game/mobfight-api';
import FightScreen from './FightScreen.vue';
import QueueDock from './QueueDock.vue';
import type { PvpStage } from '../game/pvp-stage';
import { toast } from '../game/toast';
import { consumePendingReferral } from '../game/referral-api';
import { useRegenClock, fmtEta } from '../game/regen-clock';
import { classArt, BG } from '../game/art';
import { t } from '../i18n';

import Inventory from './Inventory.vue';
import ItemModal from './ItemModal.vue';
import HeroCard from './HeroCard.vue';
import HeroDossier from './HeroDossier.vue';
import Market from './Market.vue';
import Jobs from './Jobs.vue';
import Chat from './Chat.vue';
import Rankings from './Rankings.vue';
import Dungeons from './Dungeons.vue';
import Arena from './Arena.vue';
import Settings from './Settings.vue';
import type { Nerve } from '../game/map-api';

const props = defineProps<{ user: AuthUser; onLogout: () => void }>();

const loading = ref(true);
const submitting = ref(false);
const error = ref('');
const hero = ref<HeroProfile | null>(null);
const stats = ref<EffectiveStats | null>(null);
const inventory = ref<GearInventory | null>(null);
const showTutorial = ref(true);
const view = ref<'profile' | 'map' | 'districts' | 'market' | 'jobs' | 'rankings' | 'clans' | 'dungeons' | 'pit' | 'reserve' | 'counting' | 'settings' | 'guide'>('profile');

/** THE HALL. Chat is no longer a destination — it opens over whatever screen you are
 *  on and closes again, so you never have to leave the map to answer someone. */
const hallOpen = ref(false);
// District the City Map should smoothly pan to on open ("Take it on the map").
// Cleared once the map consumes it, and on any plain nav to the map.
/**
 * THE GROUND UNDER EACH SCREEN.
 *
 * WAS a single global backdrop (BG.office) under a flat 92%/96% wash — which made
 * every screen in the game a black void with panels floating on it. Eleven scene
 * backdrops were sitting unused in the asset folder the whole time.
 *
 * Now each view stands somewhere, and the wash is a VIGNETTE instead of a flat
 * scrim: open through the upper middle where the scene reads, closing down at the
 * edges so panel rims still have something to sit against. The stops carry cold
 * blue rather than near-black, so the dark is glacier dark.
 */
const VIEW_BG: Record<string, string> = {
  profile: BG.stash,     // your own gear-room
  districts: BG.family,  // the wards, and who holds them
  market: BG.market,
  jobs: BG.jobs,
  rankings: BG.rankings,
  dungeons: BG.office,
  pit: BG.login,         // the frozen harbour BEHIND the ring — the ring panel
                         // itself already IS BG.pit, and the same painting twice
                         // on one screen read as a bug (owner: same photo three times)
  reserve: BG.shop,      // the Hoard
  counting: BG.study,    // the Weighing Stone
  settings: BG.office,
};
/**
 * Was a single `background` shorthand with `... fixed no-repeat` on this
 * scrolling shell div — `background-attachment: fixed` is the textbook iOS
 * Safari bug (the image detaches/jumps on scroll and on every address-bar
 * show/hide). Fix: the art moves to `--shell-bg`, read by a real
 * `position: fixed` `.shell::before` (app.css) — a fixed ELEMENT instead of a
 * fixed ATTACHMENT, which iOS handles correctly. The vignette wash + color
 * fallback stay right here, painted on `.shell` itself (on top of the ::before
 * art layer, same visual stack as before).
 */
const shellStyle = computed(() => {
  const art = VIEW_BG[view.value] ?? BG.office;
  return {
    '--shell-bg': `url(${art})`,
    background:
      'radial-gradient(120% 95% at 50% 22%, rgba(12,22,38,0.36) 0%, rgba(9,16,28,0.62) 46%, rgba(6,11,20,0.88) 100%), '
      + 'var(--color-bg)',
  };
});

// ── Living hero backdrop (owner follow-up on item 1): the gear-room scene is
//    a GENERATED ambient video loop (Seedance, image-to-video from the very
//    stash.webp it replaces), not just CSS drift. Painted only on the profile
//    view; the still art stays underneath as the loading/reduced-motion/other-
//    views ground, so nothing ever flashes empty.
// Read REACTIVELY, not once at load: this flag is the only thing that suppresses
// the loop, so when QA reports "the hero screen isn't animated" the OS Reduce
// Motion switch is the first suspect — and flipping it should take effect without
// a reload while that is being checked.
const motionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');
const reducedMotion = ref(motionQuery?.matches ?? false);
const onMotionPref = (e: MediaQueryListEvent) => { reducedMotion.value = e.matches; };
motionQuery?.addEventListener?.('change', onMotionPref);
onBeforeUnmount(() => motionQuery?.removeEventListener?.('change', onMotionPref));
// Leaving the game screen (sign-out) forgets the watched level, so the next
// account's first profile read is a first read rather than a level-up.
onBeforeUnmount(resetLevelWatch);

const mapFocus = ref<string | null>(null);

// ── Header sound toggle (CU-86cb58yuh item 2): one tap kills BOTH the music
//    bed and the sfx; one tap brings them back. Settings keeps its per-channel
//    switches — this is the master valve, resynced whenever the view changes
//    (Settings may have flipped a channel while we weren't looking).
const soundOn = ref(music.isEnabled() || !sfx.isMuted());
watch(view, () => { soundOn.value = music.isEnabled() || !sfx.isMuted(); });
function toggleSound() {
  const on = !soundOn.value;
  soundOn.value = on;
  music.setEnabled(on);
  sfx.setMuted(!on);
}

/**
 * A live Holmgang bout holds the shell (QA 86cb1qvpt). It is PvP: walking off
 * mid-duel left the session running with the opponent unanswered, so every
 * route out — the beam, the drawer, the settings notch, a deep link from
 * another screen — is refused while the duel is on. Arena.vue owns the flag
 * and clears it when the bout settles, is abandoned, or the component dies.
 */
const pvpStage = ref<PvpStage>(null);
const pvpLocked = computed(() => pvpStage.value !== null);
function onPvpLock(s: PvpStage) {
  pvpStage.value = s;
  if (s) menuOpen.value = false;
}
/** True when navigation was intercepted, so callers stop rather than fall
 *  through to a view change. Leaving is not refused outright (CU-86cb58yuh
 *  item 7) — it asks, then retries the blocked navigation on confirm. */
let leaveAsking = false;

/** The leave dialog. Only a LIVE bout reaches it now: a merely-found foe no
 *  longer locks the shell at all — the queue travels with the player in the
 *  corner dock (QueueDock.vue), so leaving the Pit neither cancels nor charges
 *  anything. The fee-worded drop dialog lives on the dock's own cancel. */
function leaveCopy() {
  return {
    kicker: t('battle.confirm.kicker'),
    title: t('battle.confirm.walkOutTitle'),
    note: t('battle.confirm.walkOutNote'),
    confirmLabel: t('battle.confirm.walkOutConfirm'),
    cancelLabel: t('battle.confirm.keepFighting'),
    danger: true,
  };
}

/**
 * A bout the server still considers live, resumed as a shell-level overlay.
 *
 * Every fight used to be owned by the screen that started it (CityMap, Arena,
 * Dungeons), so navigating away — or reloading — orphaned it: the session stayed
 * open server-side while the player wandered the app, and coming back to the Pit
 * looked like a brand-new bout (QA 2026-08-17). The shell now owns the resume, so
 * a live session is always on screen no matter which view the player lands in.
 */
const resumeFight = ref<FightView | null>(null);

/** Ask the server whether a bout is live. Never throws — a failed check must not
 *  wedge navigation, so it answers "nothing live" and lets the player move. */
async function liveFight(): Promise<FightView | null> {
  try { return await fetchCurrentFight(); }
  catch { return null; }
}

/** Resume whatever is live, if anything. Used on mount and after a blocked nav. */
async function reassertFight(): Promise<boolean> {
  const live = await liveFight();
  resumeFight.value = live ?? null;
  return !!live;
}

function onResumedFightDone() {
  resumeFight.value = null;
  pvpStage.value = null;
  void refresh();
}

function navBlocked(retry?: () => void): boolean {
  if (!pvpLocked.value) return false;
  if (leaveAsking) return true;
  leaveAsking = true;
  void (async () => {
    try {
      const go = await confirmAction(leaveCopy());
      if (!go) return;
      // Only a live bout locks, and leaving it is a forfeit. The QUEUE is
      // deliberately not touched here — it survives navigation by design.
      await fightAbandon().catch(() => undefined);
      pvpStage.value = null;
      // A forfeit settles the stake — the header must not keep the old silver.
      void refresh();
      retry?.();
    } finally {
      leaveAsking = false;
    }
  })();
  return true;
}

/** Views that mount their own FightScreen — if a bout is live while one of these
 *  is on screen, the fight is already visible and the shell must NOT mount a
 *  second copy of it. Anywhere else, the shell brings the bout back itself. */
const FIGHT_OWNER_VIEWS: Array<typeof view.value> = ['map', 'districts', 'dungeons', 'pit'];

async function onNav(tab: typeof view.value) {
  // The arena lock is a local flag only the Pit raises; map/garrison/heist bouts
  // have no such flag, which is how QA walked out of a live fight into the stash.
  // Ask the server rather than trusting the flag.
  if (!pvpLocked.value && !resumeFight.value) {
    const live = await liveFight();
    if (live) {
      toast(t('battle.stillFighting'), { kind: 'error' });
      // Put it back on screen unless the current view already shows it.
      if (!FIGHT_OWNER_VIEWS.includes(view.value)) resumeFight.value = live;
      return;
    }
  }
  if (navBlocked(() => void onNav(tab))) return;
  if (tab === 'map') mapFocus.value = null; // plain nav: no forced pan
  view.value = tab;
}

/** The tutorial overlay drives the backdrop behind itself so each beat
 *  previews the screen it's actually describing (Pit beat -> Pit view, ...). */
function onTutorialNav(v: string) {
  view.value = v as typeof view.value;
}

/**
 * Primary navigation — shared by the lintel beam and the mobile drawer.
 *
 * Each `rune` is the elder-futhark rune that actually MEANS the section, so the
 * beam reads as carved marks rather than decorative glyphs: ᛗ Mannaz (man/self),
 * ᛟ Othala (ancestral homestead), ᛚ Laguz (water — the wards are a fjord town),
 * ᚠ Fehu (cattle, and so wealth and trade), ᛃ Jera (the year's labour, harvest),
 * ᚨ Ansuz (the god-rune, speech), ᛊ Sowilo (sun, victory), ᚦ Thurisaz (the
 * giant, the thorn — danger), ᛏ Tiwaz (the duel).
 */
// `label` is a translation KEY, resolved at render (`t(item.label)`) so the rail
// re-letters itself the moment the tongue changes. The runes are not translated:
// they are the same Elder Futhark marks in every language.
const NAV_ITEMS = [
  { key: 'profile', label: 'nav.profile', rune: 'ᛗ' },
  { key: 'map', label: 'nav.map', rune: 'ᛟ' },
  { key: 'districts', label: 'nav.districts', rune: 'ᛚ' },
  { key: 'market', label: 'nav.market', rune: 'ᚠ' },
  { key: 'jobs', label: 'nav.jobs', rune: 'ᛃ' },
  { key: 'rankings', label: 'nav.rankings', rune: 'ᛊ' },
  // QA punch-list 07.08 item 8: clans — a tag to farm saga points under.
  { key: 'clans', label: 'nav.clans', rune: 'ᛉ' },
  { key: 'dungeons', label: 'nav.dungeons', rune: 'ᚦ' },
  { key: 'pit', label: 'nav.pit', rune: 'ᛏ' },
  // QA punch-list 07.08 (CU-86cb1qvpt): the guide must be reachable at any moment.
  { key: 'guide', label: 'nav.guide', rune: 'ᚱ' },
] as const;

// Mobile burger drawer. The burger itself is `lg:hidden`, so the drawer must
// never outlive that breakpoint: without this, opening the drawer on a narrow
// window and then widening it (or rotating a tablet to landscape) left the
// drawer sitting next to the restored desktop beam — both menus at once.
const menuOpen = ref(false);
const wideQuery = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(min-width: 1024px)')
  : null;
function closeMenuIfWide(e: MediaQueryListEvent | MediaQueryList) {
  if (e.matches) menuOpen.value = false;
}
onMounted(() => {
  // The shell never unmounts, so arming here keeps the ambient bed playing
  // across every screen (QA: "check that music sounds everywhere"). It only
  // actually starts on the first gesture — browsers refuse audio before one.
  music.armOnFirstGesture();
  if (!wideQuery) return;
  closeMenuIfWide(wideQuery);
  wideQuery.addEventListener('change', closeMenuIfWide);
});
onBeforeUnmount(() => wideQuery?.removeEventListener('change', closeMenuIfWide));
function goTo(tab: typeof view.value) {
  onNav(tab);
  menuOpen.value = false;
}
function onGotoMap(districtId?: string) {
  if (navBlocked(() => onGotoMap(districtId))) return;
  mapFocus.value = districtId ?? null;
  view.value = 'map';
}
/** Which Quarter tab to land on — set right before switching view so cash/nerve
 *  chips can deep-link into the merged commerce screen (e.g. nerve → Supplies). */
const marketInitialTab = ref<'rack' | 'street' | 'supplies' | 'sell'>('rack');
function openMarket(initialTab: typeof marketInitialTab.value = 'rack') {
  marketInitialTab.value = initialTab;
  view.value = 'market';
}

/** GUIDE SPOTLIGHT — a header currency chip routes to the store that refills
 *  that resource and briefly pulses the matching cards (`.guide-glow`), so the
 *  player lands with their eye already on the right thing. */
const guide = ref<'cash' | 'gold' | 'nerve' | null>(null);
let guideTimer = 0;
function chipTo(target: 'cash' | 'gold' | 'nerve') {
  guide.value = target;
  window.clearTimeout(guideTimer);
  guideTimer = window.setTimeout(() => { guide.value = null; }, 4200);
  if (target === 'cash') view.value = 'counting';
  else if (target === 'gold') view.value = 'reserve';
  else openMarket('supplies');
}

function onNerve(n: Nerve) {
  if (!hero.value) return;
  hero.value.hero.nerve = { current: n.current, max: n.max };
  // Map moves re-anchor the regen countdown — without this the chip's timer
  // keeps counting from the pre-spend fraction and shows a wrong ETA.
  if (n.regen) hero.value.hero.regen = { ...hero.value.hero.regen, nerve: n.regen };
}

/** Header countdowns: next nerve point / HP back to full (client-ticked). */
const nerveClock = useRegenClock(() =>
  hero.value ? { anchor: hero.value.hero.regen?.nerve, max: hero.value.hero.nerve.max } : null);

/** Item detail modal state — shared by the Stash grid, the hero-card equipped
 *  strip and the dossier paperdoll, so exactly one ItemModal exists at a time. */
const inspect = ref<{ artifact: Artifact; slot: EquipSlot } | null>(null);
/** Filter the Stash grid to a slot — set when the player clicks an empty
 *  equipped-strip slot on the hero card ("show me what I could put here"). */
const stashFilter = ref<EquipSlot | 'ALL'>('ALL');

function openInspect(artifact: Artifact, slot: EquipSlot) {
  inspect.value = { artifact, slot };
}
function onFilterRequest(slot: EquipSlot) {
  stashFilter.value = slot;
}
async function onGearUpdated(inv: GearInventory) {
  inventory.value = inv;
  inspect.value = null;
  await refresh(); // gear mutations can change cash (repair/upgrade/sell) — refresh header + stats
}

async function refreshInventory() {
  if (!hero.value) return;
  try { inventory.value = await fetchInventory(); } catch { /* non-fatal */ }
}

async function refresh() {
  try { hero.value = await fetchHero(); } catch { /* keep current */ }
  if (hero.value) { try { stats.value = await fetchStats(); } catch { /* non-fatal */ } }
  // The bag too: Market buys, fight loot and sales all land here via @updated,
  // and each of them changes the stash — without this the bought item only
  // showed up after a hard page refresh.
  await refreshInventory();
}

const form = reactive<{ name: string; cls: HeroClass | null }>({ name: '', cls: null });

/**
 * The viewport lock (h-full + .profile-main{overflow:hidden} >=1101px) exists so
 * the PROFILE's three columns can scroll inside their own panels. `view` defaults
 * to 'profile', so before a hero exists the CREATE screen was inheriting that
 * lock: full-bleed max-w-[1600px] AND a page that cannot scroll, which clipped
 * everything past the fold — the Create hero button included — with no way to
 * reach it. Creation is an ordinary scrolling column; it only applies once a
 * hero actually exists.
 */
const isProfileView = computed(() => view.value === 'profile' && !loading.value && !!hero.value);

/**
 * THE NAMING STONES — four runestones raised in a row; you choose the one whose
 * carving you want your name under.
 *
 * WAS: four WANTED POSTERS pinned to a wall, ported 1:1 from the mafia build —
 * each with a case number ("Rune-Mark No. 4811"), an "Outlawed / Hunted" ink
 * stamp, the headline "OUTLAWED", the line "Beyond the Law's Protection", a
 * rap-sheet of pips, a brass pushpin, and a sepia+contrast filter whose own code
 * comment said it existed to make the portrait "read as a 1929 police
 * photograph". Every one of those is a 20th-century police artefact. Vikings had
 * no case files; they had stones, and they carved who you were into them.
 *
 * `rune` is picked for meaning, not decoration: ᛏ Tiwaz (the war rune) for the
 * WARRIOR, ᛉ Algiz (protection) for the PALADIN, ᚱ Raido (the ride) for the
 * ARCHER, ᛈ Perthro (the lot-cup, fate and divination) for the MAGE.
 */
interface NamingStone {
  key: HeroClass;
  rune: string;
  blurb: string;
  /** Carved on the stone's shoulder — what this trade IS, in the saga's words. */
  epithet: string;
}

// `epithet` and `blurb` are translation KEYS, resolved at render. The rune is
// the same in every tongue.
const CLASSES: NamingStone[] = [
  { key: 'WARRIOR', rune: 'ᛏ', epithet: 'classes.WARRIOR.epithet', blurb: 'classes.WARRIOR.blurb' },
  { key: 'PALADIN', rune: 'ᛉ', epithet: 'classes.PALADIN.epithet', blurb: 'classes.PALADIN.blurb' },
  { key: 'ARCHER', rune: 'ᚱ', epithet: 'classes.ARCHER.epithet', blurb: 'classes.ARCHER.blurb' },
  { key: 'MAGE', rune: 'ᛈ', epithet: 'classes.MAGE.epithet', blurb: 'classes.MAGE.blurb' },
];

/**
 * The carvings under a stone: what this class is ACTUALLY created with.
 *
 * These were three invented tallies — Might, Wit, Dread — on a 1-5 scale that
 * corresponded to nothing. The game has no Might and no Wit; a hero is built
 * from attack/defense/magic/speed/hp, the guide names those Damage, Toughness,
 * Cunning and Reflexes, and the landing page was inventing a THIRD vocabulary
 * of its own (QA 2026-08-18). One table feeds all three surfaces now, and the
 * exact figure is carved next to each row — a player can hold this stone up
 * against the guide and find the same game.
 *
 * Called from the template rather than precomputed: the labels come out of the
 * catalogue, so they must re-resolve when the tongue changes.
 */
function stoneCarvings(key: HeroClass) {
  const stats = CLASS_BASE_STATS[key];
  return CLASS_STAT_ROWS.map((k) => ({
    k,
    label: t(CLASS_STAT_LABEL_KEY[k]),
    v: stats[k],
    cut: classStatPips(k, stats[k]),
  }));
}

onMounted(async () => {
  try {
    hero.value = await fetchHero();
    if (hero.value) {
      try { stats.value = await fetchStats(); } catch { /* non-fatal */ }
      await refreshInventory();
      // A bout left open by a reload / a closed tab / a URL jump belongs back on
      // screen immediately — not silently ticking towards its expiry while the
      // player browses (QA 2026-08-17).
      void reassertFight();
    }
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }

  // Referral bonus — claims a code captured pre-auth (see main.ts). Silent no-op
  // when there's nothing pending, already attempted, or the claim fails.
  const reward = await consumePendingReferral();
  if (reward != null) {
    toast(t('heroScreen.referralBonus', { amount: reward }), { icon: cashIcon });
    if (hero.value) await refresh(); // pick up the credited cash on the header chip
  }
});

async function submit() {
  error.value = '';
  if (form.name.trim().length < 2) { error.value = t('hero.create.errName'); return; }
  if (!form.cls) { error.value = t('hero.create.errClass'); return; }
  submitting.value = true;
  try {
    hero.value = await createHero(form.name.trim(), form.cls);
    toast(t('heroScreen.welcome', { name: hero.value.hero.name }), { icon: classArt(form.cls) });
    // Full refresh, not just the bag: `stats` must stop being null right away
    // or the hero card renders base stats until the first unrelated refetch.
    await refresh();
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div class="shell h-screen flex flex-col text-text overflow-hidden" :style="shellStyle">
    <!-- The living hall — generated ambient loop over the still art (profile only).
         muted+playsinline so mobile autoplay is allowed; the ::before still art
         stays beneath as poster/fallback. -->
    <video
      v-if="view === 'profile' && !reducedMotion"
      class="shell-live" :src="liveStash"
      autoplay muted loop playsinline disablepictureinpicture
      aria-hidden="true" tabindex="-1"
    ></video>
    <!-- ══ THE LINTEL — the carved header beam ═══════════════════════════════
         Two stacked objects, replacing the single flex-wrap row that used to hold
         wordmark + tabs + coin + sign-out all on one line:

           .hdr-band — an IRON STRAP. Who you are, what you hold, how you leave.
           .hdr-beam — the TIMBER BEAM the section notches are cut into.

         The beam SCROLLS instead of wrapping. The old rail wrapped to a second row
         between 1024–1280px (its own comment called that out) — a nav rail can
         wrap and still look intentional, but a load-bearing beam cannot: a beam
         that breaks in half is a broken beam. `hdr-rail` hides the scrollbar. -->
    <header class="hdr-lintel">
      <div class="hdr-band">
        <img :src="wordmark" alt="Fimbulwake" class="hdr-mark" />

        <!-- MOBILE burger — right edge of the strap -->
        <button
          class="hdr-burger lg:hidden ml-auto"
          @click="menuOpen = true" :aria-label="t('nav.openMenu')"
        >
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        <!-- COIN SLOTS — own full row below lg, inline (pushed right) on desktop -->
        <div v-if="hero" class="w-full flex items-center gap-2 lg:contents">
          <button class="chip-rune lg:ml-auto" :title="t('heroScreen.getSilver')" @click="chipTo('cash')">
            <img :src="cashIcon" alt="silver" class="h-6 w-6 object-contain shrink-0" />
            <span class="font-heading text-gold">{{ hero.progression.cash.toLocaleString() }}</span>
          </button>
          <button class="chip-rune" :title="t('heroScreen.buyRunes')" @click="chipTo('gold')">
            <img :src="runesIcon" alt="runes" class="h-6 w-6 object-contain shrink-0" />
            <span class="font-heading text-gold-bright">{{ hero.progression.goldBars.toLocaleString() }}</span>
          </button>
          <button class="chip-rune" :title="nerveClock && !nerveClock.full ? t('heroScreen.furyNext', { time: fmtEta(nerveClock.nextIn) }) : t('heroScreen.refillFury')" @click="chipTo('nerve')">
            <img :src="nerveIcon" alt="fury" class="h-6 w-6 object-contain shrink-0" />
            <span class="font-heading text-text whitespace-nowrap">{{ hero.hero.nerve.current }}/{{ hero.hero.nerve.max }}<span v-if="nerveClock && !nerveClock.full" class="font-body text-[11px] tabular-nums text-text-dim"> · {{ fmtEta(nerveClock.nextIn) }}</span></span>
          </button>
          <!-- SOUND — the master valve; Settings keeps the per-channel switches -->
          <button
            class="chip-rune shrink-0 ml-auto lg:ml-0"
            :title="soundOn ? t('heroScreen.soundOff') : t('heroScreen.soundOn')"
            :aria-label="soundOn ? t('heroScreen.soundOff') : t('heroScreen.soundOn')"
            :aria-pressed="soundOn"
            @click="toggleSound"
          >
            <svg v-if="soundOn" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" stroke="none" />
              <path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 6a9 9 0 0 1 0 12" />
            </svg>
            <svg v-else class="w-5 h-5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M11 5 6 9H3v6h3l5 4V5z" fill="currentColor" stroke="none" />
              <line x1="16" y1="9" x2="22" y2="15" /><line x1="22" y1="9" x2="16" y2="15" />
            </svg>
          </button>
        </div>

        <!-- DESKTOP sign out -->
        <button
          class="hidden lg:inline-flex btn-forge btn-forge--blood btn-forge--sm shrink-0"
          @click="props.onLogout"
        >{{ t('nav.signOut') }}</button>
      </div>

      <!-- the beam: one notch per section, chiselled in -->
      <nav class="hdr-beam hdr-rail hidden lg:flex" :aria-label="t('heroScreen.sections')">
        <template v-if="hero">
          <button
            v-for="item in NAV_ITEMS" :key="item.key"
            @click="onNav(item.key)"
            :title="pvpLocked && view !== item.key ? t('heroScreen.pvpLockedTitle') : undefined"
            :class="['notch-tab shrink-0', view === item.key && 'notch-tab-active', pvpLocked && view !== item.key && 'is-barred']"
          ><span class="notch-rune" aria-hidden="true">{{ item.rune }}</span>{{ t(item.label) }}</button>
        </template>
        <button
          :class="['notch-tab shrink-0', view === 'settings' && 'notch-tab-active', pvpLocked && view !== 'settings' && 'is-barred']"
          :title="t('nav.settingsTitle')"
          @click="onNav('settings')"
        ><span class="notch-rune" aria-hidden="true">ᛞ</span>{{ t('nav.settings') }}</button>
      </nav>
    </header>

    <!-- ── MOBILE MENU DRAWER (burger) ─────────────────────────────────── -->
    <Transition name="drawer">
      <div v-if="menuOpen" class="hdr-drawer-scrim lg:hidden" @click.self="menuOpen = false">
        <aside class="hdr-drawer" role="dialog" :aria-label="t('nav.menu')">
          <div class="hdr-drawer-head">
            <img :src="wordmark" alt="Fimbulwake" class="hdr-mark" />
            <button class="hdr-drawer-x" @click="menuOpen = false" :aria-label="t('nav.closeMenu')">
              <svg class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </div>
          <nav class="hdr-drawer-nav">
            <template v-if="hero">
              <button
                v-for="item in NAV_ITEMS" :key="item.key"
                :class="['hdr-drawer-item', view === item.key && 'is-active', pvpLocked && view !== item.key && 'is-barred']"
                @click="goTo(item.key)"
              ><span class="hdr-drawer-rune" aria-hidden="true">{{ item.rune }}</span>{{ t(item.label) }}</button>
            </template>
            <button
              :class="['hdr-drawer-item', view === 'settings' && 'is-active', pvpLocked && view !== 'settings' && 'is-barred']"
              @click="goTo('settings')"
            ><span class="hdr-drawer-rune" aria-hidden="true">ᛞ</span>{{ t('nav.settings') }}</button>
          </nav>
          <button class="btn-forge btn-forge--blood btn-forge--sm hdr-drawer-out" @click="props.onLogout">{{ t('nav.signOut') }}</button>
        </aside>
      </div>
    </Transition>

    <!-- CITY MAP — full-screen, fills the viewport below the top bar -->
    <div v-if="!loading && hero && view === 'map'" class="flex-1 min-h-0">
      <CityMap
        :hero-class="hero?.hero.class" :hero-level="hero?.progression.level" :hero-id="hero?.hero.id"
        :hero-hp="hero?.hero.stats.hp" :hero-max-hp="stats?.effective.maxHp ?? hero?.hero.stats.maxHp"
        :hero-full-hp="stats?.base.maxHp" :hero-hp-regen="hero?.hero.regen?.hp"
        :cash="hero?.progression.cash" :gold-bars="hero?.progression.goldBars" :focus-district="mapFocus"
        @nerve="onNerve" @updated="refresh" @focused="mapFocus = null" @wards="onNav('districts')"
      />
    </div>

    <!-- every other view — scrollable centered column; the PROFILE view instead
         locks to the viewport (page never scrolls — columns manage their own
         height, the stash scrolls inside its panel) down to the 1100px
         single-column breakpoint, where it falls back to page scroll. -->
    <main v-else class="flex-1 min-h-0" :class="isProfileView ? 'profile-main' : 'overflow-y-auto'">
      <div :class="isProfileView ? 'h-full max-w-[1600px] mx-auto px-6 py-4' : 'max-w-[1100px] mx-auto px-6 py-10'">
      <p v-if="loading" class="text-text-dim">{{ t('heroScreen.loading') }}</p>

      <!-- error -->
      <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>

      <!-- CREATE (settings stays reachable even before the hero exists) -->
      <section v-if="!loading && !hero && view !== 'settings'" class="make">
        <header class="make-head">
          <p class="make-kicker">{{ t('heroScreen.naming.kicker') }}</p>
          <h1 class="make-title gilt-text">{{ t('heroScreen.naming.heading') }}</h1>
          <span class="make-rule"></span>
          <p class="make-sub">{{ t('heroScreen.naming.sub') }}</p>
        </header>

        <!-- the chisel line — a slot cut in stone, not a typed case-file field -->
        <div class="chisel">
          <label class="chisel-label" for="hero-name">{{ t('hero.create.nameLabel') }}</label>
          <input
            id="hero-name"
            v-model="form.name"
            maxlength="50"
            :placeholder="t('heroScreen.naming.placeholder')"
            class="chisel-input"
          />
        </div>

        <!-- THE STONES. A radio group, not buttons — "one of four" is exactly what
             a radiogroup is for, and it hands us arrow-key nav + the right AT
             semantics for free. (Kept from the old board: the semantics were the
             one thing about it that was right.) -->
        <fieldset class="stones">
          <legend class="sr-only">{{ t('heroScreen.naming.pickTrade') }}</legend>
          <label
            v-for="c in CLASSES" :key="c.key"
            class="stone"
            :class="{ 'stone--on': form.cls === c.key }"
          >
            <input
              class="sr-only stone-radio"
              type="radio"
              name="hero-class"
              :value="c.key"
              v-model="form.cls"
            />
            <span class="slab">
              <span class="slab-rune" aria-hidden="true">{{ c.rune }}</span>
              <span class="relief">
                <img :src="classArt(c.key)" :alt="classLabel(c.key)" />
              </span>
              <span class="slab-name">{{ classLabel(c.key) }}</span>
              <span class="slab-epithet">{{ t(c.epithet) }}</span>
              <span class="slab-blurb">{{ t(c.blurb) }}</span>
              <!-- tally marks chiselled in a row, replacing the rap-sheet's
                   ●●●○○ pips. Real elements, so a cut mark can be lit by aurora
                   on the chosen stone; a text glyph could only change colour.
                   The marks are aria-hidden: they are a redraw of the figure
                   carved beside them, and five grooves read aloud say nothing. -->
              <span class="tallies">
                <span class="tally" v-for="s in stoneCarvings(c.key)" :key="s.k">
                  <span class="tally-k">{{ s.label }}</span>
                  <span class="tally-marks" aria-hidden="true">
                    <i v-for="n in 5" :key="n" :class="n <= s.cut && 'is-cut'"></i>
                  </span>
                  <em class="tally-n">{{ s.v }}</em>
                </span>
              </span>
            </span>
          </label>
        </fieldset>

        <button
          :disabled="submitting"
          @click="submit"
          class="btn-forge btn-forge--rune btn-forge--lg"
        >{{ submitting ? t('hero.create.submitting') : t('hero.create.submit') }}</button>
      </section>

      <!-- PROFILE — the kit and the stash side by side, the reckoning beneath.
           WAS three columns of small stacked panels, which is the browser-RPG stat
           sheet this game inherited: the SHAPE was the tell, not the styling. -->
      <section v-else-if="!loading && hero && view === 'profile'" class="profile-grid">
        <HeroCard
          class="pg-kit"
          :hero="hero" :stats="stats" :inventory="inventory"
          @inspect="openInspect" @filter-request="onFilterRequest" @updated="refresh"
        />

        <div class="pg-stash min-w-0 min-h-0 flex flex-col">
          <Inventory
            v-if="inventory" :inventory="inventory" :filter="stashFilter"
            @update:filter="stashFilter = $event" @inspect="openInspect" @updated="refresh"
          />
        </div>

        <HeroDossier class="pg-tally" :hero="hero" :stats="stats" :inventory="inventory" />
      </section>

      <!-- DISTRICTS -->
      <Districts v-else-if="!loading && hero && view === 'districts'" @updated="refresh" @goto-map="onGotoMap" />

      <!-- THE QUARTER (merged Market + Shop) -->
      <Market
        v-else-if="!loading && hero && view === 'market'"
        :initial-tab="marketInitialTab"
        :spotlight-item="guide === 'nerve' ? 'nerve_refill' : null"
        @updated="refresh"
        @topup="view = 'reserve'"
        @counting="view = 'counting'"
      />

      <!-- THE COUNTING HOUSE — bars → cash, full page in the Gold Reserve's design -->
      <CountingHouse
        v-else-if="!loading && hero && view === 'counting'"
        :spotlight="guide === 'cash'"
        @updated="refresh"
        @topup="view = 'reserve'"
      />

      <!-- THE GOLD RESERVE — full page, structurally dominion's premium ShopPage -->
      <GoldReserve
        v-else-if="!loading && hero && view === 'reserve'"
        :balance="hero?.progression.goldBars"
        :spotlight="guide === 'gold'"
        @updated="refresh"
      />

      <!-- JOBS -->
      <Jobs v-else-if="!loading && hero && view === 'jobs'" @updated="refresh" />


      <!-- RANKINGS -->
      <Rankings v-else-if="!loading && hero && view === 'rankings'" />

      <Clans v-else-if="!loading && hero && view === 'clans'" />

      <!-- DUNGEONS / HEISTS -->
      <!-- Live fury goes DOWN so the door can warn about an unaffordable raid
           before the server bounces it; 'supplies' comes back UP when the
           warning's way-out button routes to the stall (same as the nerve chip). -->
      <Dungeons
        v-else-if="!loading && hero && view === 'dungeons'"
        :hero-class="hero?.hero.class"
        :nerve="hero?.hero.nerve"
        :nerve-regen-per-min="hero?.hero.regen?.nerve.perMin"
        @updated="refresh"
        @supplies="chipTo('nerve')"
      />

      <!-- THE PIT (arena) — interactive tactical bout vs a bot -->
      <Arena v-else-if="!loading && hero && view === 'pit'" :hero-class="hero?.hero.class" @updated="refresh" @locked="onPvpLock" />

      <!-- SAGA GUIDE — why play, the loop, every screen explained (QA punch-list 07.08) -->
      <SagaGuide v-else-if="!loading && view === 'guide'" @back="view = 'profile'" />

      <!-- SETTINGS — account, papers (KYC), purchases (dominion's Settings page) -->
      <Settings
        v-else-if="!loading && view === 'settings'"
        :user="props.user" :hero="hero"
        @goto-reserve="view = 'reserve'"
      />

      <!-- THE QUEUE DOCK — the Holmgang queue riding along in the corner on
           every screen but the Pit itself (there the ring shows the same state
           full-size). FIGHT routes back to the Pit, whose mount-time resume
           picks up the freshly-accepted session.
           AFTER the view chain, deliberately: its own v-if must never sit
           between the v-else-if screens — it did once, and every screen below
           it (Guide, Settings) silently became its else-branch and vanished. -->
      </div>
    </main>

    <!-- THE QUEUE DOCK — the Holmgang queue riding along in the corner on every
         screen but the Pit itself (there the ring shows the same state
         full-size). FIGHT routes back to the Pit, whose mount-time resume picks
         up the freshly-accepted session.

         OUTSIDE both branches, deliberately. It used to live at the end of the
         view chain inside `<main v-else>` — which is the ELSE of the city map,
         so on the one screen a player is most likely to wander off to while
         searching, the dock did not exist at all (owner 02.09: "search hides
         behind the minimap"; the store kept searching, nothing was drawn). Here
         it is a sibling of both, so it renders on every view, and `lifted` can
         do its job of riding above the map's minimap. -->
    <QueueDock v-if="view !== 'pit'" :lifted="view === 'map'" @fight="view = 'pit'" @updated="refresh" />

    <!-- ══ THE WEATHER ══ the Fimbulwinter itself, over every screen. Two snow
         layers falling at different speeds and angles (parallax is what sells
         depth), and the aurora breathing at the top of the sky. Foreground snow
         drifting OVER the panels is a deliberate genre device — it makes the UI
         sit IN the world instead of on top of a picture of it. pointer-events
         none; z-index 90 keeps it under every drawer, dock and modal. -->
    <div class="wx" aria-hidden="true">
      <span class="wx-aurora"></span>
      <span class="wx-snow wx-snow--far"></span>
      <span class="wx-snow wx-snow--near"></span>
    </div>

    <!-- ══ THE HALL ══ a pull that sticks up out of the bottom-left corner, and a
         small window over it. Chat was a nav page, then a full-height drawer; both
         made hearing someone cost you the screen you were on. A dock costs a corner. -->
    <div class="hall-dock">
      <Transition name="hw">
        <div v-if="hallOpen" class="hall-win" role="dialog" :aria-label="t('heroScreen.theHall')">
          <header class="hall-win-head">
            <span class="hall-win-rune" aria-hidden="true">ᚨ</span>
            <h2 class="hall-win-t">{{ t('heroScreen.theHall') }}</h2>
            <span class="hall-win-gap"></span>
            <button class="btn-forge btn-forge--carved btn-forge-icon" :aria-label="t('hall.close')" @click="hallOpen = false">&times;</button>
          </header>
          <Chat />
        </div>
      </Transition>

      <button
        class="hall-pull" :class="{ 'is-open': hallOpen }"
        :aria-expanded="hallOpen"
        :title="hallOpen ? t('hall.close') : t('hall.open')"
        @click="hallOpen = !hallOpen"
      >
        <span class="hall-pull-rune" aria-hidden="true">ᚨ</span>
        <span class="hall-pull-t">{{ t('hall.label') }}</span>
        <span class="hall-pull-chev" aria-hidden="true">{{ hallOpen ? '▾' : '▴' }}</span>
      </button>
    </div>

    <ToastHost />
    <LevelUpBanner />
    <ConfirmHost />

    <!-- First-session onboarding overlay (self-hides once completed/skipped).
         It drives `view` behind itself so each beat previews its own screen
         (Pit step shows the Pit, map steps show the City Map, ...) and hands
         the app back to the default Hero view once it's done. -->
    <Tutorial
      v-if="hero && (showTutorial || tutorialLaunch.open)"
      @navigate="onTutorialNav"
      @updated="refresh"
      @done="showTutorial = false; view = 'profile'"
    />

    <!-- A bout the server still has open — resumed here so it is impossible to
         be "in a fight" while looking at another screen. -->
    <FightScreen
      v-if="resumeFight"
      :initial="resumeFight"
      :hero-class="hero?.hero.class"
      @done="onResumedFightDone"
    />

    <!-- Shared item detail modal — one instance for the stash grid, hero-card
         equipped strip and dossier paperdoll alike. -->
    <ItemModal
      v-if="inspect"
      :artifact="inspect.artifact"
      :slot="inspect.slot"
      :is-equipped="inventory?.equipped[inspect.slot]?.id === inspect.artifact.id"
      :equipped-in-slot="inventory?.equipped[inspect.slot] ?? null"
      @updated="onGearUpdated"
      @close="inspect = null"
    />
  </div>
</template>

<style scoped>
/* ── Carve your name — the Naming Stones ────────────────────────────────────
   WAS: "the Althing's outlawry board" — four wanted posters on aged paper,
   pinned to a dark wall with brass pushpins, each stamped OUTLAWED / HUNTED,
   carrying a case number and a rap sheet, the portrait run through
   `sepia(.5) contrast(1.06)` to (its own comment) "read as a 1929 police
   photograph", and a wax MADE slam on the one you picked.

   NOW: four runestones raised at the thing-place. The portrait is carved INTO
   the stone (`mix-blend-mode: luminosity` — the figure keeps the rock's own hue,
   which is what a relief does) and only steps into full colour on the stone you
   choose. That colour-arrival replaces the wax stamp as the "you picked this
   one" beat, and it says something the stamp never could: the man in the stone
   wakes up when you take his trade.

   NOTE: these styles are SFC-scoped and CANNOT borrow landing.css's tokens —
   those resolve only under #landing-root. Stone carries literal values. */
.make-head { margin-bottom: 1.5rem; }
.make-kicker {
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 10px;
  /* 0.24em -> 0.12em: wide-tracked small-caps is the deco tell (see app.css) */
  letter-spacing: 0.07em; text-transform: uppercase;
  color: color-mix(in srgb, var(--color-aurora) 58%, transparent);
  margin-bottom: 6px;
}
.make-title {
  font-family: var(--font-heading, serif);
  font-size: clamp(30px, 3.4vw, 42px);
  line-height: 1.02;
  color: #c8d3e2;
  letter-spacing: 0.02em;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.8), 0 2px 0 rgba(226, 236, 248, 0.07);
}
.make-rule {
  display: block; width: 64px; height: 2px; margin: 10px 0 12px;
  background: linear-gradient(90deg, var(--color-aurora), transparent);
}
.make-sub { font-family: var(--font-body, serif); color: var(--color-text-dim); }

/* the chisel line — a slot cut into stone. Capped: a name is a few words. */
.chisel { max-width: 21rem; margin-bottom: 2rem; }
.chisel-label {
  display: block; margin-bottom: 5px;
  font-family: var(--font-label, monospace);
  font-size: 10px; font-weight: 700;
  letter-spacing: 0.07em; text-transform: uppercase;
  color: var(--color-text-dim);
}
.chisel-input {
  width: 100%;
  padding: 0.62rem 0.8rem;
  background: linear-gradient(180deg, #0a0e13, #12171f 60%, #0b0f14);
  border: 0;
  border-radius: 0;
  clip-path: var(--clip-hewn);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.8), inset 0 -1px 0 rgba(226, 236, 248, 0.07);
  color: var(--color-text);
  font-family: var(--font-body, serif);
  font-size: 15px;
  transition: box-shadow 180ms ease;
}
.chisel-input::placeholder { color: var(--color-text-dim); opacity: 0.65; }
.chisel-input:focus {
  outline: none;
  /* light arrives in the cut — the same affordance as every button and notch */
  box-shadow:
    inset 0 2px 4px rgba(0, 0, 0, 0.8),
    inset 0 0 16px color-mix(in srgb, var(--color-aurora) 20%, transparent),
    inset 0 -1px 0 color-mix(in srgb, var(--color-aurora) 55%, transparent);
}

/* the thing-place */
.stones {
  border: 0; padding: 0; margin: 0 0 2rem;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem 1rem;
  align-items: end; /* stones stand on one ground line, whatever their height */
}
/* NOTE: the narrow-viewport lying-slab layout lives at the BOTTOM of this block,
   not here. `.slab > * { display:block }` and `.slab { display:block }` are
   declared below at the SAME specificity, so a media query placed here would
   lose on source order alone and silently do nothing. (Same trap the old poster
   block documented — the layout changed, the cascade hazard did not.) */

.stone { position: relative; display: block; cursor: pointer; }
/* clip-path clips an outer box-shadow away entirely, so the stone's drop shadow
   has to be a filter — filter runs after clip-path and follows the cut shape. */
.stone { filter: drop-shadow(0 16px 26px rgba(0, 0, 0, 0.62)); transition: filter 320ms ease; }
.stone-radio:focus-visible + .slab { outline: 2px solid var(--color-aurora); outline-offset: 4px; }

.slab {
  position: relative; display: block; overflow: hidden;
  padding: 20px 14px 16px;
  text-align: center;
  /* the shouldered arch — a real runestone silhouette, flat foot to stand on */
  clip-path: var(--clip-stele);
  /* granite: cool grey body, two mineral blooms, top-lit */
  background:
    var(--tex-stone),
    radial-gradient(120% 60% at 22% 8%, rgba(226, 236, 248, 0.07), transparent 46%),
    radial-gradient(90% 55% at 88% 96%, rgba(0, 0, 0, 0.28), transparent 52%),
    linear-gradient(180deg, #3b414b 0%, #2c323b 42%, #1d222a 100%);
  background-size: 190px 190px, auto, auto, auto;
  background-blend-mode: soft-light, normal, normal, normal;
  transition: transform 380ms cubic-bezier(0.2, 0.8, 0.25, 1), background 380ms ease;
}
/* the two passes that stop granite reading as a flat swatch: mineral speckle,
   then a raking-light shade. Same turbulence trick the old paper used — the
   technique was sound, only the material was wrong. */
.slab::before {
  content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none;
  background:
    /* the plait chased under the arch and along the foot */
    var(--orn-cord-dark) repeat-x left top 9px,
    var(--orn-cord-lite) repeat-x left top 10px,
    var(--orn-cord-dark) repeat-x left bottom 7px,
    var(--orn-cord-lite) repeat-x left bottom 6px;
  opacity: 0.5;
}
.slab::after {
  content: ''; position: absolute; inset: 0; z-index: 1; pointer-events: none;
  box-shadow:
    inset 0 0 30px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(226, 236, 248, 0.12),
    inset 0 -2px 6px rgba(0, 0, 0, 0.55);
}
.slab > * { position: relative; z-index: 2; display: block; }

.stone:hover .slab { transform: translateY(-4px); }
.stone:hover { filter: drop-shadow(0 22px 34px rgba(0, 0, 0, 0.7)); }
.stone--on .slab {
  transform: translateY(-6px);
  background:
    var(--tex-stone),
    radial-gradient(120% 60% at 22% 8%, rgba(111, 216, 192, 0.14), transparent 48%),
    radial-gradient(90% 55% at 88% 96%, rgba(0, 0, 0, 0.26), transparent 52%),
    linear-gradient(180deg, #414954 0%, #303845 42%, #1f252e 100%);
}
.stone--on {
  filter:
    drop-shadow(0 24px 38px rgba(0, 0, 0, 0.72))
    drop-shadow(0 0 22px rgba(111, 216, 192, 0.28));
}

/* the rune cut into the arch */
.slab-rune {
  font-family: var(--font-heading, serif);
  font-size: 30px; line-height: 1;
  color: #10151b;
  margin-bottom: 8px;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.7), 0 1px 0 rgba(226, 236, 248, 0.16);
  transition: color 320ms ease, text-shadow 320ms ease;
}
.stone--on .slab-rune {
  color: var(--color-aurora);
  text-shadow: 0 0 16px color-mix(in srgb, var(--color-aurora) 70%, transparent),
               0 -1px 0 rgba(0, 0, 0, 0.8);
}

/* the relief. `luminosity` keeps the stone's own hue and takes only the
   portrait's light, which is exactly what a carving is. On the chosen stone the
   blend drops away and the figure arrives in full colour — this is the screen's
   whole payoff beat, and it replaces the old wax MADE stamp. */
.relief::after {
  content: ''; position: absolute; inset: 0; z-index: 2; pointer-events: none;
  background:
    var(--orn-cord-dark) repeat-x left top 2px,
    var(--orn-cord-lite) repeat-x left top 3px,
    var(--orn-cord-dark) repeat-x left bottom 3px,
    var(--orn-cord-lite) repeat-x left bottom 2px;
  opacity: 0.5;
}
.relief {
  position: relative;
  margin: 0 auto 10px;
  aspect-ratio: 1 / 1.06;
  overflow: hidden;
  background: linear-gradient(180deg, #262c34, #171c23);
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.55), inset 0 3px 8px rgba(0, 0, 0, 0.5);
}
.relief img {
  width: 100%; height: 100%;
  object-fit: cover; object-position: 50% 0%;
  mix-blend-mode: luminosity;
  opacity: 0.82;
  filter: grayscale(1) contrast(1.22) brightness(1.04);
  transition: opacity 460ms ease, filter 460ms ease, transform 560ms ease;
}
.stone:hover .relief img { opacity: 0.94; }
.stone--on .relief img {
  mix-blend-mode: normal;
  opacity: 1;
  filter: none;
  transform: scale(1.04);
}
/* frost rim on the woken figure */
.stone--on .relief {
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--color-aurora) 45%, transparent),
    inset 0 0 18px color-mix(in srgb, var(--color-aurora) 18%, transparent);
}

.slab-name {
  font-family: var(--font-heading, serif);
  font-weight: 700; font-size: 19px;
  text-transform: uppercase; letter-spacing: 0.02em;
  color: #b3bfd0; line-height: 1;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85), 0 1px 0 rgba(226, 236, 248, 0.1);
  transition: color 320ms ease, text-shadow 320ms ease;
}
.stone--on .slab-name {
  color: var(--color-aurora);
  text-shadow: 0 0 14px color-mix(in srgb, var(--color-aurora) 55%, transparent),
               0 -1px 0 rgba(0, 0, 0, 0.85);
}
.slab-epithet {
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 9px;
  letter-spacing: 0.07em; text-transform: uppercase;
  color: #a4b4c7;
  margin-top: 4px;
}
.slab-blurb {
  font-family: var(--font-body, serif);
  font-style: italic; font-size: 13px; line-height: 1.35;
  color: #a8b4c4;
  margin: 7px 0 11px;
}

/* tally marks — chiselled strokes, deepened and lit on the chosen stone.
   Five rows now, each ending in its real figure, so the block is a grid: marks
   and numbers hold one column across every row while the label column takes
   only the width the tongue needs (Polish "Wytrzymałość" is twice the English).
   `display: contents` keeps .tally as the v-for grouping element without it
   becoming a grid item and swallowing the shared columns. */
.tallies {
  display: grid; grid-template-columns: 1fr auto auto;
  align-items: center; gap: 4px 8px;
  border-top: 1px solid rgba(0, 0, 0, 0.5);
  box-shadow: inset 0 1px 0 rgba(226, 236, 248, 0.07);
  padding-top: 8px;
}
.tally {
  display: contents;
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 9px;
  letter-spacing: 0.07em; text-transform: uppercase;
  color: #a4b4c7;
}
/* display:contents drops the .tally box, so its type styles have to land on the
   cells themselves — they are the elements the grid actually lays out. */
.tally-k, .tally-n {
  font-family: var(--font-label, monospace);
  font-weight: 700; font-size: 9px;
  letter-spacing: 0.07em; text-transform: uppercase;
  color: #a4b4c7;
}
.tally-k { white-space: nowrap; }
.tally-n {
  font-style: normal; font-size: 10px; letter-spacing: 0.02em;
  color: #d6e2f0; min-width: 22px; text-align: right;
  font-variant-numeric: tabular-nums;
}
.stone--on .tally-n { color: #eaf3ff; }
.tally-marks { display: inline-flex; gap: 3px; align-items: center; }
.tally-marks i {
  display: block; width: 2px; height: 11px;
  /* an uncut mark is a shallow scratch; a cut one is a real groove */
  background: rgba(0, 0, 0, 0.34);
  box-shadow: 1px 0 0 rgba(226, 236, 248, 0.06);
  transform: skewX(-9deg);
  transition: background 320ms ease, box-shadow 320ms ease;
}
.tally-marks i.is-cut {
  background: #0d1116;
  box-shadow: 1px 0 0 rgba(226, 236, 248, 0.16);
}
.stone--on .tally-marks i.is-cut {
  background: var(--color-aurora);
  box-shadow: 0 0 7px color-mix(in srgb, var(--color-aurora) 75%, transparent);
}

/* Below 4-across a standing stone cannot fit the viewport — two rows of them
   push CARVE THE STONE under the fold with no way to reach it. So the stone LIES
   DOWN into a lintel slab: relief left, carvings right. Same granite, same
   relief blend, ~a third of the height. MUST stay last: it overrides `.slab` and
   `.slab > *` at equal specificity, so it only wins on source order. */
@media (max-width: 1023.98px), (max-height: 900px) {
  .stones { grid-template-columns: minmax(0, 1fr); gap: 0.7rem; align-items: stretch; }
  .slab {
    display: grid;
    grid-template-columns: 22px 74px minmax(0, 1fr);
    grid-template-areas: 'rune relief name' 'rune relief blurb' 'rune relief tally';
    align-content: center;
    gap: 1px 12px;
    padding: 10px 14px;
    text-align: left;
    /* a lying slab has no arch to show — square it off, keep the hewn edge */
    clip-path: var(--clip-hewn);
  }
  .slab-rune { grid-area: rune; align-self: center; font-size: 20px; margin: 0; }
  .relief { grid-area: relief; margin: 0; align-self: center; aspect-ratio: 1 / 1.1; }
  .slab-name { grid-area: name; font-size: 16px; }
  /* the epithet is the first thing to go: at strip height the trade name plus one
     line of blurb is all that earns its row */
  .slab-epithet { display: none; }
  .slab-blurb { grid-area: blurb; margin: 3px 0 5px; font-size: 12px; }
  /* One row per carving, same three columns as the standing stone. Pairing them
     two-up fits the English labels and then breaks: "Wytrzymałość" is one
     unbreakable word that overran the marks in a half-width cell. Five short
     rows cost ~20px of slab height; a clipped stat name costs the fix. */
  .tallies {
    grid-area: tally; border-top: 0; box-shadow: none; padding-top: 2px;
    gap: 2px 8px;
  }
  .tally-k, .tally-n { font-size: 8px; }
  .tally-n { min-width: 18px; }
  .stone:hover .slab, .stone--on .slab { transform: translateY(-2px); }
}

/* A short-but-wide window (a 1462x768 laptop is the common case) has width to
   spare but no height — stand them two-up instead of one long column, which
   halves the row count again and clears the fold. */
@media (min-width: 760px) and (max-height: 900px) {
  .stones { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.7rem 0.9rem; }
}

@media (prefers-reduced-motion: reduce) {
  .slab, .relief img, .slab-rune, .slab-name, .tally-marks i, .stone { transition: none; }
  .stone:hover .slab, .stone--on .slab { transform: none; }
}

/* ── Hero page ────────────────────────────────────────────────────────────
   ONE VIEWPORT on desktop (owner call): the main pane locks its scroll, the
   kit fills the left, the sea-chest scrolls INSIDE its own panel on the
   right, the reckoning runs as a strip along the foot. Narrow or short
   screens fall back to the stacked scrolling column — a phone cannot hold
   this in one screen and shouldn't pretend to. */
.profile-main { overflow-y: auto; }

.profile-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas: 'kit' 'stash' 'tally';
  gap: 1.25rem;
  padding-bottom: 2.5rem;
}
.pg-kit { grid-area: kit; }
.pg-stash { grid-area: stash; min-height: 380px; }
.pg-tally { grid-area: tally; }

/* The panels ARRIVE (CU-86cbc65ny item 2) — kit, then sea-chest, then dossier,
   each rising out of the dark a beat apart, so opening the hero page reads as
   the hall lighting up rather than a form snapping into place. Entrance only:
   the animation ends where the layout already is, and reduced-motion players
   get the finished screen immediately. */
.pg-kit, .pg-stash, .pg-tally { animation: pg-arrive 480ms cubic-bezier(0.2, 0.8, 0.25, 1) backwards; }
.pg-stash { animation-delay: 110ms; }
.pg-tally { animation-delay: 220ms; }
@keyframes pg-arrive {
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .pg-kit, .pg-stash, .pg-tally { animation: none; }
}

/* THE ONE-VIEWPORT LOCK.
   The height floor is 800px, NOT 600px, and it must stay in lockstep with
   HeroCard.vue's complementary `(max-height: 799px)` — `hero-viewport-lock.test.ts`
   fails if the two ever drift apart.

   600px was measured wrong (QA 86cb9yd3p item 3, "no adaptation of the hero
   page"). Locking only asks whether the pane CAN be one screen; nobody checked
   whether one screen was ENOUGH. Measured on the real page at 1920 wide, the
   kit's socket rails need more height than the arena is handed:

     viewport   kit    arena   rails   rails overflow by   tag collisions
       700      297     104     160          56                  8
       760      357     164     173           9                  4
       800      417     224     218           0                  0
      1000      597     404     398           0                  0

   Between 600 and 800 the rails — `justify-content: center` with rigid children
   in a `min-height: 0` flex item — spilled out of the arena in both directions
   and landed on top of the vitals: HELM/MAIL/WEAPON over the health gauge, the
   figure over the whole foot band. A 1920x700 window is an ordinary laptop with
   the browser un-maximised, which is exactly what QA shot. */
@media (min-width: 1101px) and (min-height: 800px) {
  .profile-main { overflow: hidden; }
  .profile-grid {
    height: 100%;
    grid-template-columns: minmax(0, 1.15fr) minmax(360px, 0.85fr);
    grid-template-rows: minmax(0, 1fr) auto;
    grid-template-areas: 'kit stash' 'tally tally';
    gap: 14px;
    padding-bottom: 4px;
  }
  .pg-kit { min-height: 0; }
  .pg-stash { min-height: 0; }
  .pg-tally { min-height: 0; }
}


/* ── THE WEATHER ──────────────────────────────────────────────────────────────
   Seamless loops: each layer animates background-position to exact multiples of
   its own tile size, so the wrap is invisible. The far layer falls slow and near
   vertical; the near layer is bigger, faster and wind-blown — two speeds is the
   entire parallax. */
.wx {
  position: fixed; inset: 0; z-index: 90;
  pointer-events: none;
}
.wx-aurora {
  position: absolute; inset: -8% 0 55%;
  background:
    radial-gradient(52% 68% at 24% 0%, rgba(111, 216, 192, 0.13), transparent 68%),
    radial-gradient(46% 60% at 62% 0%, rgba(111, 176, 232, 0.10), transparent 66%),
    radial-gradient(38% 52% at 86% 4%, rgba(168, 146, 224, 0.09), transparent 64%);
  animation: wx-breathe 11s ease-in-out infinite;
  mix-blend-mode: screen;
}
@keyframes wx-breathe {
  0%, 100% { opacity: 0.55; transform: translateX(0); }
  50% { opacity: 1; transform: translateX(-1.5%); }
}
.wx-snow { position: absolute; inset: -20% -5% -5%; }
.wx-snow--far {
  background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='260' height='260' viewBox='0 0 260 260'><circle cx='118' cy='146' r='1.4' fill='%23dfefff' opacity='0.58'/><circle cx='132' cy='153' r='0.8' fill='%23dfefff' opacity='0.61'/><circle cx='164' cy='206' r='0.8' fill='%23dfefff' opacity='0.50'/><circle cx='24' cy='211' r='1.3' fill='%23dfefff' opacity='0.37'/><circle cx='255' cy='251' r='1.2' fill='%23dfefff' opacity='0.66'/><circle cx='41' cy='4' r='1.1' fill='%23dfefff' opacity='0.38'/><circle cx='49' cy='63' r='0.7' fill='%23dfefff' opacity='0.58'/><circle cx='115' cy='219' r='1.1' fill='%23dfefff' opacity='0.67'/><circle cx='130' cy='172' r='1.1' fill='%23dfefff' opacity='0.49'/><circle cx='259' cy='259' r='1.4' fill='%23dfefff' opacity='0.70'/><circle cx='82' cy='60' r='0.9' fill='%23dfefff' opacity='0.39'/><circle cx='199' cy='104' r='1.4' fill='%23dfefff' opacity='0.54'/><circle cx='249' cy='220' r='0.7' fill='%23dfefff' opacity='0.45'/><circle cx='237' cy='122' r='1.5' fill='%23dfefff' opacity='0.55'/><circle cx='19' cy='164' r='1.3' fill='%23dfefff' opacity='0.48'/><circle cx='23' cy='86' r='1.5' fill='%23dfefff' opacity='0.73'/><circle cx='31' cy='64' r='0.8' fill='%23dfefff' opacity='0.38'/><circle cx='207' cy='46' r='1.1' fill='%23dfefff' opacity='0.57'/><circle cx='50' cy='190' r='0.8' fill='%23dfefff' opacity='0.67'/><circle cx='30' cy='109' r='0.9' fill='%23dfefff' opacity='0.48'/><circle cx='252' cy='209' r='0.9' fill='%23dfefff' opacity='0.79'/><circle cx='55' cy='103' r='1.4' fill='%23dfefff' opacity='0.67'/><circle cx='26' cy='257' r='0.9' fill='%23dfefff' opacity='0.48'/><circle cx='201' cy='86' r='0.9' fill='%23dfefff' opacity='0.39'/><circle cx='23' cy='152' r='0.9' fill='%23dfefff' opacity='0.65'/><circle cx='97' cy='118' r='1.5' fill='%23dfefff' opacity='0.59'/></svg>");
  background-size: 260px 260px;
  opacity: 0.5;
  animation: wx-fall-far 38s linear infinite;
}
.wx-snow--near {
  background-image: url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='320' height='320' viewBox='0 0 320 320'><circle cx='184' cy='277' r='1.8' fill='%23dfefff' opacity='0.43'/><circle cx='291' cy='262' r='1.8' fill='%23dfefff' opacity='0.44'/><circle cx='237' cy='301' r='1.8' fill='%23dfefff' opacity='0.83'/><circle cx='282' cy='193' r='2.0' fill='%23dfefff' opacity='0.40'/><circle cx='12' cy='308' r='1.8' fill='%23dfefff' opacity='0.70'/><circle cx='82' cy='264' r='2.2' fill='%23dfefff' opacity='0.50'/><circle cx='56' cy='231' r='1.7' fill='%23dfefff' opacity='0.46'/><circle cx='179' cy='273' r='2.2' fill='%23dfefff' opacity='0.49'/><circle cx='294' cy='65' r='1.6' fill='%23dfefff' opacity='0.48'/><circle cx='143' cy='19' r='1.8' fill='%23dfefff' opacity='0.53'/><circle cx='183' cy='42' r='2.0' fill='%23dfefff' opacity='0.80'/><circle cx='314' cy='210' r='2.3' fill='%23dfefff' opacity='0.64'/></svg>");
  background-size: 320px 320px;
  opacity: 0.6;
  filter: blur(0.6px);
  animation: wx-fall-near 16s linear infinite;
}
@keyframes wx-fall-far { from { background-position: 0 0; } to { background-position: -260px 780px; } }
@keyframes wx-fall-near { from { background-position: 0 0; } to { background-position: -640px 960px; } }
@media (prefers-reduced-motion: reduce) {
  .wx-snow, .wx-aurora { animation: none; }
  .wx-snow--near { display: none; }
}

/* ── THE HALL DOCK ────────────────────────────────────────────────────────────
   A pull that sticks up out of the bottom-left corner of the viewport, and a small
   window that rises over it. Chat has now been three things: a nav destination (cost
   one of nine slots), a full-height drawer (cost the whole screen), and this — which
   costs a corner. It is the only one of the three you can leave open while you play.

   The pull's bottom 7px sit BELOW the viewport edge on purpose: a tab that runs off
   the screen reads as something you grab, where a tab that floats above the edge
   reads as a button someone forgot to align. */
.hall-dock {
  position: fixed;
  left: 22px;
  bottom: 0;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
  pointer-events: none;   /* the gap between window and pull stays click-through */
  /* the home-indicator safe area on notched phones — without it the pull's
     intentional 7px overhang (see .hall-pull below) sits under the gesture bar */
  padding-bottom: env(safe-area-inset-bottom, 0);
}
.hall-dock > * { pointer-events: auto; }

.hall-pull {
  position: relative;
  display: inline-flex; align-items: center; gap: 8px;
  padding: 9px 16px 15px;
  margin-bottom: -7px;    /* the part that runs off the bottom edge */
  border: 0;
  clip-path: polygon(9px 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%, 0 9px);
  background: var(--tex-forge), var(--mat-beam);
  background-size: 150px 150px, auto;
  background-blend-mode: soft-light, normal;
  box-shadow:
    var(--rim-ice),
    inset 0 1px 0 rgba(201, 165, 88, 0.4),
    0 -6px 20px rgba(0, 0, 0, 0.55);
  font-family: var(--font-label, sans-serif);
  font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase;
  color: #bccbdb;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85);
  transition: color 170ms ease, transform 140ms ease;
}
/* the cord chased along its head, so the pull is the same object as the beams */
.hall-pull::after {
  content: ''; position: absolute; inset: 0; pointer-events: none; opacity: 0.42;
  background:
    var(--orn-cord-dark) repeat-x left top 2px,
    var(--orn-cord-lite) repeat-x left top 3px;
}
.hall-pull:hover { color: var(--color-aurora); transform: translateY(-2px); }
.hall-pull.is-open { color: var(--color-aurora); }
.hall-pull.is-open::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--color-aurora) 55%, transparent),
              inset 0 0 18px color-mix(in srgb, var(--color-aurora) 16%, transparent);
}
/* Explicit box for the rune. None of the self-hosted faces (Cinzel / Cormorant
   SC / Alegreya) covers Elder Futhark, so ᚨ resolves to whatever system font the
   OS offers — which differs per platform and brings its own advance width and
   baseline. Pinning the slot keeps the chip's geometry identical everywhere
   instead of shifting with the fallback. */
.hall-pull-rune {
  flex: none;
  display: inline-grid; place-items: center;
  width: 13px; height: 13px; line-height: 1;
  font-family: var(--font-heading, serif); font-size: 13px;
}
/* Longer translations ("A Csarnok", "Die Halle") must not wrap inside a chip
   that hangs off the viewport edge. */
.hall-pull-t { white-space: nowrap; }
.hall-pull-chev { flex: none; font-size: 9px; color: #8fa0b3; }

/* the window. Small on purpose — it is a corner of the screen, not a room. */
.hall-win {
  display: flex; flex-direction: column;
  width: min(92vw, 366px);
  height: min(58vh, 430px);
  padding: 0 10px 10px;
  background: var(--surface-plank);
  background-size: var(--surface-plank-size);
  background-blend-mode: var(--surface-plank-blend);
  box-shadow: var(--rim-ice), 0 -14px 44px rgba(0, 0, 0, 0.66);
}
.hall-win-head {
  display: flex; align-items: center; gap: 8px;
  flex: none;
  padding: 11px 2px 9px;
  border-bottom: 3px solid transparent;
  border-image: var(--mat-iron) 1;
}
.hall-win-rune { font-family: var(--font-heading, serif); font-size: 14px; color: var(--color-aurora); }
.hall-win-t {
  font-family: var(--font-heading, serif); font-size: 16px;
  color: #d3dfee;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.9), 0 1px 0 rgba(207, 232, 255, 0.09);
}
.hall-win-gap { flex: 1 1 auto; }

/* it rises out of the pull rather than fading in */
.hw-enter-active, .hw-leave-active { transition: opacity 180ms ease, transform 200ms cubic-bezier(0.2, 0.8, 0.25, 1); transform-origin: 0 100%; }
.hw-enter-from, .hw-leave-to { opacity: 0; transform: translateY(14px) scaleY(0.94); }
@media (prefers-reduced-motion: reduce) {
  .hw-enter-active, .hw-leave-active { transition: opacity 150ms ease; }
  .hw-enter-from, .hw-leave-to { transform: none; }
}
/* on a phone the dock hugs the edge and the window takes the width it can get */
@media (max-width: 640px) {
  .hall-dock { left: 10px; }
  .hall-win { width: calc(100vw - 20px); height: min(62vh, 400px); }
}

/* Barred while a holmgang is live — the section is still legible (the player
   must be able to read where they are), it just cannot be walked to. */
.is-barred {
  opacity: 0.4;
  cursor: not-allowed;
  filter: saturate(0.5);
}

/* ── Mobile burger + drawer ───────────────────────────────────────────── */
/* hidden on desktop (scoped rule beats Tailwind's lg:hidden); shown <1024px */
.hdr-burger {
  display: none; place-items: center;
  width: 38px; height: 38px; flex: none;
  color: #a9b6c8;
  border: 0;
  border-radius: 0;
  clip-path: var(--clip-hewn);
  background: linear-gradient(180deg, #0b0f14, #12171f 55%, #0a0e13);
  box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.75), inset 0 -1px 0 rgba(226, 236, 248, 0.07);
  transition: color 150ms ease, box-shadow 200ms ease;
}
@media (max-width: 1023.98px) { .hdr-burger { display: grid; } }
@media (min-width: 1024px) { .hdr-drawer-scrim { display: none; } }
.hdr-burger:hover {
  color: var(--color-aurora);
  box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.75),
              inset 0 0 12px color-mix(in srgb, var(--color-aurora) 20%, transparent);
}
.hdr-burger:active { transform: translateY(1px); }

.hdr-drawer-scrim {
  position: fixed; inset: 0; z-index: 300;
  background: rgba(4, 3, 8, 0.62); backdrop-filter: blur(3px);
  display: flex; justify-content: flex-end;
}
.hdr-drawer {
  width: min(82vw, 320px); height: 100%;
  display: flex; flex-direction: column;
  padding: 14px 14px 18px;
  background: var(--mat-plank);
  border-left: 3px solid transparent;
  border-image: var(--mat-iron) 1;
  box-shadow: -18px 0 50px rgba(0, 0, 0, 0.66);
  overflow-y: auto;
}
.hdr-drawer-head {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 12px; margin-bottom: 8px;
  border-bottom: 3px solid transparent;
  border-image: var(--mat-iron) 1;
}
.hdr-drawer-x {
  display: grid; place-items: center; width: 34px; height: 34px;
  min-width: 44px; min-height: 44px;
  color: var(--color-text-dim); border-radius: 0;
}
.hdr-drawer-x:hover { color: var(--color-text); background: rgba(255, 255, 255, 0.05); }
.hdr-drawer-nav { display: flex; flex-direction: column; gap: 2px; flex: 1; }
/* Drawer entries as CARVED PLANKS — the mobile twin of the lintel's notches, so
   the two navigations are visibly the same object at two sizes. Was "speakeasy
   ledger lines" with a gold ◆ marker; a diamond pin is deco jewellery, and the
   drawer was the last place in the shell still wearing one.

   The rune lives in the markup (`.hdr-drawer-rune`) rather than a ::before
   content string, because each section has its OWN rune — a pseudo-element can
   only ever paint one shared glyph, which is exactly why the old version had to
   fall back to a generic diamond for all nine rows. */
.hdr-drawer-item {
  position: relative;
  display: flex; align-items: center; gap: 12px;
  text-align: left;
  padding: 13px 14px;
  font-family: var(--font-heading, serif);
  font-size: 16px; letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #78849a;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85), 0 1px 0 rgba(226, 236, 248, 0.07);
  transition: color 160ms ease;
}
/* the cut this row sits in */
.hdr-drawer-item::before {
  content: '';
  position: absolute; inset: 2px 4px; z-index: -1;
  clip-path: var(--clip-hewn);
  background: linear-gradient(180deg, #0c1015, #131922);
  box-shadow: inset 0 2px 3px rgba(0, 0, 0, 0.7);
  opacity: 0; transition: opacity 170ms ease;
}
/* the chisel wedge on the open row — pointing IN from the leading edge, the
   vertical-list equivalent of the beam's wedge pointing down */
.hdr-drawer-item::after {
  content: '';
  position: absolute; left: 0; top: 50%;
  width: 6px; height: 14px;
  transform: translateY(-50%) scaleX(0);
  transform-origin: 0 50%;
  clip-path: polygon(0 0, 100% 50%, 0 100%);
  background: var(--mat-aurora);
  filter: drop-shadow(0 0 5px color-mix(in srgb, var(--color-aurora) 75%, transparent));
  transition: transform 200ms cubic-bezier(0.2, 1.4, 0.4, 1);
}
.hdr-drawer-rune {
  flex: none; width: 1.1em;
  font-size: 15px; line-height: 1;
  color: #4a5666;
  transition: color 170ms ease, text-shadow 170ms ease;
}
.hdr-drawer-item:hover { color: #c3cddb; }
.hdr-drawer-item:hover::before { opacity: 0.7; }
.hdr-drawer-item.is-active {
  color: var(--color-aurora);
  text-shadow: 0 0 12px color-mix(in srgb, var(--color-aurora) 50%, transparent),
               0 -1px 0 rgba(0, 0, 0, 0.9);
}
.hdr-drawer-item.is-active::before {
  opacity: 1;
  background: linear-gradient(180deg, #0a1418 0%, #0d1d22 55%, #102a2c 100%);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.75),
              inset 0 0 14px color-mix(in srgb, var(--color-aurora) 22%, transparent);
}
.hdr-drawer-item.is-active::after { transform: translateY(-50%) scaleX(1); }
.hdr-drawer-item.is-active .hdr-drawer-rune {
  color: var(--color-aurora);
  text-shadow: 0 0 10px color-mix(in srgb, var(--color-aurora) 65%, transparent);
}
.hdr-drawer-out { width: 100%; margin-top: 14px; }

/* slide-in transition */
.drawer-enter-active, .drawer-leave-active { transition: opacity 200ms ease; }
.drawer-enter-active .hdr-drawer, .drawer-leave-active .hdr-drawer { transition: transform 220ms cubic-bezier(0.2, 0.8, 0.25, 1); }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
.drawer-enter-from .hdr-drawer, .drawer-leave-to .hdr-drawer { transform: translateX(100%); }
@media (prefers-reduced-motion: reduce) {
  .drawer-enter-active .hdr-drawer, .drawer-leave-active .hdr-drawer { transition: none; }
}
</style>
